import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import { DataTable, DataTablePageEvent, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useApiClientsOrNull } from 'design-system/my-lib';
import './AdminListPage.scss';

interface PlatformAdminEntry {
  identityId: string;
  email: string | null;
  displayName: string | null;
}

const DEFAULT_ROWS = 50;
const ROWS_PER_PAGE = [25, 50, 100];
const COPIED_FEEDBACK_MS = 1500;

const AdminListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();

  const [admins, setAdmins] = useState<PlatformAdminEntry[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!apiClients?.organizationClient) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await apiClients.organizationClient.get(
          '/admin/platform-admins',
          {
            params: {
              limit: rows,
              offset: first,
            },
          }
        );
        if (cancelled) return;
        setAdmins(response.admins);
        setTotalRecords(response.total);
      } catch {
        if (cancelled) return;
        setError(t('admin.admins.errors.fetchFailed'));
        setAdmins([]);
        setTotalRecords(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, first, rows, t]);

  const handlePageChange = useCallback((event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  }, []);

  const handleRowClick = useCallback(
    (event: DataTableRowClickEvent) => {
      const row = event.data as PlatformAdminEntry;
      if (row?.identityId) {
        navigate(`/admin/metasphere/identities/${row.identityId}`);
      }
    },
    [navigate]
  );

  const handleCopy = useCallback(
    async (identityId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      try {
        await navigator.clipboard.writeText(identityId);
        setCopiedId(identityId);
        globalThis.setTimeout(() => {
          setCopiedId((current) => (current === identityId ? null : current));
        }, COPIED_FEEDBACK_MS);
      } catch (err) {
        console.error('[AdminListPage] clipboard.writeText failed', err);
      }
    },
    []
  );

  const displayNameTemplate = useCallback(
    (rowData: PlatformAdminEntry) => <span>{rowData.displayName || '—'}</span>,
    []
  );

  const emailTemplate = useCallback(
    (rowData: PlatformAdminEntry) => <span>{rowData.email || '—'}</span>,
    []
  );

  const identityIdTemplate = useCallback(
    (rowData: PlatformAdminEntry) => {
      const isCopied = copiedId === rowData.identityId;
      return (
        <div className="admin-list-page__identity-cell">
          <code className="admin-list-page__identity-id">
            {rowData.identityId}
          </code>
          <Button
            icon={isCopied ? 'pi pi-check' : 'pi pi-copy'}
            className="p-button-text p-button-sm admin-list-page__copy-btn"
            aria-label={
              isCopied
                ? t('admin.admins.identityIdCopied')
                : t('admin.admins.copyIdentityId')
            }
            tooltip={
              isCopied
                ? t('admin.admins.identityIdCopied')
                : t('admin.admins.copyIdentityId')
            }
            onClick={(e) => handleCopy(rowData.identityId, e)}
            data-testid={`admin-copy-${rowData.identityId}`}
          />
        </div>
      );
    },
    [copiedId, handleCopy, t]
  );

  return (
    <div className="admin-list-page" data-testid="admin-list-page">
      <header className="admin-list-page__header">
        <h1 className="admin-list-page__title">
          {t('admin.admins.title')}
        </h1>
      </header>

      {error ? (
        <Message
          severity="error"
          text={error}
          className="admin-list-page__error"
        />
      ) : (
        <DataTable
          value={admins}
          loading={loading}
          paginator
          rows={rows}
          rowsPerPageOptions={ROWS_PER_PAGE}
          first={first}
          onPage={handlePageChange}
          totalRecords={totalRecords}
          lazy
          onRowClick={handleRowClick}
          rowClassName={() => 'admin-list-page__row'}
          emptyMessage={t('admin.admins.empty')}
          dataKey="identityId"
          stripedRows
          className="admin-list-page__table"
          data-testid="admin-list-table"
        >
          <Column
            field="email"
            header={t('admin.admins.columns.email')}
            body={emailTemplate}
          />
          <Column
            field="displayName"
            header={t('admin.admins.columns.displayName')}
            body={displayNameTemplate}
          />
          <Column
            field="identityId"
            header={t('admin.admins.columns.identityId')}
            body={identityIdTemplate}
          />
        </DataTable>
      )}
    </div>
  );
};

export default AdminListPage;
